import { Card, CardSection, Flex, FormField, FormHelp, LoadingButton, Select, Textarea } from '@zillow/constellation';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { useCreateCommentMutation } from 'API/order/comments/CommentsApi';
import { commentSchema, INewComment } from 'components/order/comments/commentSchema';
import { Field, FieldProps, Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import styled from 'styled-components';
import { eCommentScope } from 'types/order/comment/eCommentScope';
import { eCommentType, prettyCommentsType } from 'types/order/comment/eCommentType';
import { internalCommentTypes } from 'types/order/comment/internalCommentTypes';
import { assertDefined } from 'utilities/Assertions';
import { StyledCardHeader } from './StyledCardHeader';
import { ISelectOption } from '../../../../ui-toolkit';

const StyledCard = styled(Card)({
  width: '100%',
  height: '100%',
  minHeight: '340px',
  border: 'none',
});

interface IQuickCommentFormProps {
  dialogControls: React.ReactNode;
}
export const QuickCommentForm: React.FC<IQuickCommentFormProps> = ({ dialogControls }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId, 'Order ID is undefined');

  const [createInternalComment, { isLoading }] = useCreateCommentMutation();
  const onSubmit = async ({ content, type }: INewComment<eCommentType>) => {
    const response = await createInternalComment({
      orderId,
      content,
      type,
      scope: eCommentScope.internal,
    });
    return checkForMutationSuccess(response, 'Comment Created Successfully!');
  };

  const options: ISelectOption<eCommentType>[] = useMemo(
    () =>
      internalCommentTypes.map((type) => ({
        value: type,
        label: prettyCommentsType[type],
      })),
    []
  );
  const initialValue: { content: string; type: eCommentType | '' } = { content: '', type: '' };

  return (
    <StyledCard cardType={'secondary'} cardElevation={0}>
      <StyledCardHeader marginBottom={0}>
        <Flex display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
          {dialogControls}
          Leave a Comment
        </Flex>
      </StyledCardHeader>
      <CardSection padding={0} margin={0}>
        <Formik
          initialValues={initialValue}
          validateOnMount
          validationSchema={commentSchema<eCommentType>()}
          onSubmit={async ({ content, type }, formikHelpers) => {
            if (type === '') return;
            try {
              // Validation says these are required
              const result = await onSubmit({ content: content!, type: type! });
              if (result) {
                formikHelpers.resetForm({ values: { content: '', type: '' } });
                formikHelpers.validateForm();
              }
            } catch (e: any) {}
          }}
        >
          {({ isValid }) => (
            <Form>
              <Flex display={'flex'} flexDirection={'column'} gap={1}>
                <Field name={'content'}>
                  {({ field, meta }: FieldProps) => (
                    <FormField
                      error={!!meta.error && meta.touched}
                      description={<FormHelp>{meta.touched && meta.error}</FormHelp>}
                    >
                      <Textarea {...field} placeholder={`Comment`} rows={5} />
                    </FormField>
                  )}
                </Field>
                <Field name={'type'}>
                  {({ field, meta }: FieldProps) => (
                    <FormField
                      error={!!meta.error && meta.touched}
                      description={<FormHelp>{meta.touched && meta.error}</FormHelp>}
                    >
                      <Select {...field} placeholder={`Comment Type`}>
                        <option value={''}>Comment Type</option>
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                  )}
                </Field>
                <LoadingButton buttonType={'primary'} type={'submit'} loading={isLoading} disabled={!isValid}>
                  Submit
                </LoadingButton>
              </Flex>
            </Form>
          )}
        </Formik>
      </CardSection>
    </StyledCard>
  );
};
